'use client';

import type { Dispatch, MutableRefObject, SetStateAction } from 'react';
import {
  buildViewportMetrics,
  commitViewportMetrics,
  measureRawViewportDimensions,
  type ViewportMetrics,
} from './viewport-metrics-measurement';

const KEYBOARD_HEIGHT_THRESHOLD = 150;
const WIDTH_CHANGE_THRESHOLD = 1;

interface StableViewportRefs {
  stableHeightRef: MutableRefObject<number>;
  stableWidthRef: MutableRefObject<number>;
}

interface ViewportMetricsSyncInput extends StableViewportRefs {
  setMetrics: Dispatch<SetStateAction<ViewportMetrics>>;
}

interface KeyboardResizeInput {
  rawHeight: number;
  rawWidth: number;
  stableHeight: number;
  stableWidth: number;
}

interface ViewportMetricsRafSync {
  cancelPendingFrame: () => void;
  sync: () => void;
}

export function primeViewportMetrics({
  stableHeightRef,
  stableWidthRef,
  setMetrics,
}: ViewportMetricsSyncInput): void {
  const { rawWidth, rawHeight } = measureRawViewportDimensions();
  stableWidthRef.current = rawWidth;
  stableHeightRef.current = rawHeight;
  commitViewportMetrics({
    setMetrics,
    nextMetrics: buildViewportMetrics({ rawWidth, rawHeight }),
  });
}

export function createViewportMetricsRafSync(
  syncInput: ViewportMetricsSyncInput,
): ViewportMetricsRafSync {
  let rafId = 0;

  return {
    cancelPendingFrame: () => {
      if (rafId) {
        cancelAnimationFrame(rafId);
      }
    },
    sync: () => {
      if (rafId) {
        cancelAnimationFrame(rafId);
      }

      rafId = requestAnimationFrame(() => {
        syncViewportMetrics(syncInput);
      });
    },
  };
}

export function addViewportMetricsListeners(sync: () => void): () => void {
  const onWindowResize = (): void => sync();
  const onOrientationChange = (): void => sync();
  const onVisualViewportResize = (): void => sync();
  const visualViewport = window.visualViewport;

  window.addEventListener('resize', onWindowResize, { passive: true });
  window.addEventListener('orientationchange', onOrientationChange);
  visualViewport?.addEventListener('resize', onVisualViewportResize);

  return () => {
    window.removeEventListener('resize', onWindowResize);
    window.removeEventListener('orientationchange', onOrientationChange);
    visualViewport?.removeEventListener('resize', onVisualViewportResize);
  };
}

function syncViewportMetrics({
  stableHeightRef,
  stableWidthRef,
  setMetrics,
}: ViewportMetricsSyncInput): void {
  const { rawWidth, rawHeight } = measureRawViewportDimensions();
  const isKeyboard = isKeyboardViewportResize({
    rawWidth,
    rawHeight,
    stableWidth: stableWidthRef.current,
    stableHeight: stableHeightRef.current,
  });

  if (isKeyboard) {
    commitViewportMetrics({
      setMetrics,
      nextMetrics: buildViewportMetrics({
        rawWidth,
        rawHeight: stableHeightRef.current,
      }),
    });
    return;
  }

  stableWidthRef.current = rawWidth;
  stableHeightRef.current = rawHeight;
  commitViewportMetrics({
    setMetrics,
    nextMetrics: buildViewportMetrics({ rawWidth, rawHeight }),
  });
}

function isKeyboardViewportResize({
  rawHeight,
  rawWidth,
  stableHeight,
  stableWidth,
}: KeyboardResizeInput): boolean {
  const widthChanged = Math.abs(rawWidth - stableWidth) > WIDTH_CHANGE_THRESHOLD;
  const heightDrop = stableHeight - rawHeight;

  return !widthChanged && heightDrop > KEYBOARD_HEIGHT_THRESHOLD;
}
